/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import IDate from '@dvrd/idate';

export type FieldDefinition = {
    expectedType: ModelFieldType;
    convertToBoolean: boolean;
    allowNull?: boolean;
    allowUndefined?: boolean;
    defaultValue?: any;
};

export enum ModelFieldType {
    STRING,
    NUMBER,
    BOOLEAN,
    OBJECT,
    ARRAY,
    BIGINT
}

export const createFieldDef = (
    expectedType: ModelFieldType,
    defaultValue?: any,
    allowNull: boolean = false,
    allowUndefined: boolean = false,
    convertToBoolean: boolean = false
): FieldDefinition => ({
    expectedType,
    allowUndefined,
    allowNull,
    convertToBoolean,
    defaultValue
});

export interface BaseFields {
    id: bigint | string | number;
    created_at: string;
    updated_at: string;
}

export interface BaseProps {
    id?: bigint | string | number;
    created_at?: string;
    updated_at?: string;
}

export abstract class BaseModel<Props extends BaseProps, Fields extends BaseFields> extends Object {
    private readonly fieldFilter: Array<keyof Fields>;
    protected readonly fields: Fields;
    protected readonly props: Props;
    private readonly FieldsDefinition: Record<string, FieldDefinition>;
    private readonly tempFields: Record<string, any>;
    protected abstract build: (data?: Props) => void;

    protected constructor(
        fieldFilter: Array<keyof Fields>,
        fieldsDefinition: Record<string, FieldDefinition>,
        fields: Fields,
        props?: Props
    ) {
        super(props);
        this.fields = {...fields};
        this.props = props || ({} as Props);
        this.fieldFilter = fieldFilter;
        this.FieldsDefinition = fieldsDefinition;
        this.tempFields = {};
    }

    setField = (key: keyof Fields, value: any) => {
        if (Object.hasOwn(this.fields, key)) this.fields[key] = value;
        return this;
    };

    getField = (key: keyof Fields): any | null => {
        if (Object.hasOwn(this.fields, key)) return this.fields[key];
        return null;
    };

    setTempField = (key: string, value: any) => {
        this.tempFields[key] = value;
    };

    getTempField = (key: string): any | null => {
        if (key in this.tempFields) return this.tempFields[key];
        return null;
    };

    deleteTempField = (key: string) => {
        delete this.tempFields[key];
    };

    get id(): string | bigint | number {
        return this.fields.id;
    }

    set id(id: string | bigint | number) {
        this.fields.id = id;
    }

    get createdAt(): string {
        return this.fields.created_at;
    }

    set createdAt(created_at: string) {
        this.fields.created_at = created_at;
    }

    get createdAtMoment(): IDate {
        return new IDate(this.createdAt);
    }

    get updatedAt(): string {
        return this.fields.updated_at;
    }

    set updatedAt(updated_at: string) {
        this.fields.updated_at = updated_at;
    }

    get updatedAtMoment(): IDate | null {
        if (!this.fields.updated_at) return null;
        return new IDate(this.updatedAt);
    }

    protected constructFields = (data = this.props) => {
        const preparedData = this.prepareData(data);
        const keys = Object.keys(this.fields).filter(key => !this.fieldFilter.includes(key as keyof Fields));
        for (const key of keys) this.initializeField(key, preparedData[key]);
    };

    private initializeField = (key: string, value: any) => {
        if (Object.hasOwn(this.fields, key) && value !== undefined) (this.fields as Record<string, any>)[key] = value;
    };

    private prepareData = (data: Props) => {
        const prepareKeys = Object.keys(this.FieldsDefinition),
            preparedData: Record<string, any> = {};
        for (const key of prepareKeys) {
            const definition: FieldDefinition = this.FieldsDefinition[key],
                {allowNull, allowUndefined, defaultValue} = definition;
            const value = data[key as keyof Props];
            if ((value === null && !allowNull) || (value === undefined && !allowUndefined)) {
                // `if (defaultValue)` treated null/0/'' as "no default declared" and
                // fell through to determineDefaultValue (e.g. for STRING that meant '').
                // Use explicit `!== undefined` so a declared `null` default is honored.
                if (defaultValue !== undefined) preparedData[key] = defaultValue;
                else preparedData[key] = this.determineDefaultValue(definition);
            } else if ((value === null && allowNull) || (value === undefined && (allowUndefined || allowNull))) {
                preparedData[key] = null;
            } else preparedData[key] = this.validateExistingValue(value, definition);
        }
        return preparedData;
    };

    private determineDefaultValue = (definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition,
            typeToUse = convertToBoolean ? 'boolean' : expectedType;
        switch (typeToUse) {
            case ModelFieldType.STRING:
                return '';
            case ModelFieldType.NUMBER:
                return 0;
            case ModelFieldType.BIGINT:
                return 0n;
            case ModelFieldType.BOOLEAN:
                return false;
            case ModelFieldType.OBJECT:
                return {};
            case ModelFieldType.ARRAY:
                return new Array<any>();
        }
        return null;
    };

    private validateExistingValue = (value: any, definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition;
        if (convertToBoolean && (value === true || value === false)) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                if (typeof value !== 'string') value = value.toString();
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.NUMBER:
                if (typeof value !== 'number') value = Number(value);
                if (Number.isNaN(value)) value = 0;
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.BIGINT:
                if (typeof value !== 'bigint') value = BigInt(value);
                return value;
            case ModelFieldType.BOOLEAN:
                if (value !== true && value !== false) value = false;
                return value;
            case ModelFieldType.OBJECT:
                // typeof null === 'object' AND Array is object → exclude both.
                // Previous `try { Object.keys(value).length }` was a no-op for primitives
                // (Object.keys(5) returns [] in ES6+, never throws), so primitives leaked
                // through as the field value instead of being coerced to {}.
                if (value === null || typeof value !== 'object' || Array.isArray(value)) value = {};
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.ARRAY:
                if (!Array.isArray(value)) value = [];
                return this.convertBoolean(value, expectedType, convertToBoolean);
            default:
                return null;
        }
    };

    private convertBoolean = (value: any, expectedType: ModelFieldType, shouldConvert: boolean = false): any => {
        if (!shouldConvert) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                return value === 'true';
            case ModelFieldType.NUMBER:
                return value === 1;
            case ModelFieldType.BIGINT:
                return value === 1n;
            case ModelFieldType.BOOLEAN:
                return value;
            case ModelFieldType.OBJECT:
                try {
                    return Object.keys(value).length > 0;
                } catch {
                    return false;
                }
            case ModelFieldType.ARRAY:
                try {
                    return value.length > 0;
                } catch {
                    return false;
                }
        }
        return false;
    };
}
